import { minute, type TimeEntry, type TimeEntryPublishedInfo } from "@pentacode/core";

export type RosterChangeType = "added" | "changed" | "removed";

interface RosterChange {
    before: TimeEntryPublishedInfo | null;
    after: TimeEntryPublishedInfo;
    seen: Date;
}

const CHANGE_HIGHLIGHT_DURATION = 10 * minute;

/**
 * Remembers the published changes an employee has not acknowledged yet, so they stay highlighted for a while
 * even after viewing them marks the entries as seen.
 */
export class RosterChanges {
    private _changes = new Map<string, RosterChange>();

    track(entries: TimeEntry[], now = new Date()) {
        for (const entry of entries) {
            if (!entry.published) {
                continue;
            }

            const change = this._changes.get(entry.id);
            if (!change || !entry.isSeen) {
                this._changes.set(entry.id, {
                    before: entry.seen,
                    after: entry.published,
                    seen: now,
                });
            }
        }
    }

    forget(entryId: string) {
        this._changes.delete(entryId);
    }

    getType(entryId: string, date: string, profileId: number | undefined, now = Date.now()): RosterChangeType | null {
        const change = this._changes.get(entryId);

        if (!profileId || !change || now - change.seen.getTime() > CHANGE_HIGHLIGHT_DURATION) {
            return null;
        }

        if (
            change.after.employeeId === profileId &&
            change.after.date === date &&
            !change.after.deleted &&
            (!change.before ||
                change.before.date !== change.after.date ||
                change.before.employeeId !== change.after.employeeId)
        ) {
            return "added";
        }

        if (
            change.before &&
            change.before.employeeId === profileId &&
            change.before.date === date &&
            !change.before.deleted &&
            (change.after.deleted || change.after.date !== date || change.after.employeeId !== profileId)
        ) {
            return "removed";
        }

        if (
            change.before &&
            (change.before.startPlanned?.getTime() !== change.after.startPlanned?.getTime() ||
                change.before.endPlanned?.getTime() !== change.after.endPlanned?.getTime() ||
                change.before.positionId !== change.after.positionId ||
                change.before.comment !== change.after.comment)
        ) {
            return "changed";
        }

        return null;
    }

    /** The version of the entry shown crossed out for a "changed" or "removed" entry. */
    getPrevious(entry: TimeEntry): TimeEntryPublishedInfo | null {
        // `entry.seen` is overwritten once the entry is marked seen, so prefer the snapshot taken when tracking
        const change = this._changes.get(entry.id);
        return change ? change.before : entry.seen;
    }
}

export function getEntriesToMarkSeen(entries: TimeEntry[], visibleEntryIds: string[], profileId: number) {
    return [...new Set(visibleEntryIds)]
        .map((id) => entries.find((e) => e.id === id))
        .filter((entry): entry is TimeEntry => !!entry && entry.employeeId === profileId && !entry.isSeen);
}

export function canOfferShiftForTrade(startPlanned: Date | null | undefined, now = new Date()) {
    return !!startPlanned && startPlanned.getTime() > now.getTime();
}
